/* Free-entry (no-purchase) opt-in form.
 *
 * Two-step flow against the opt-in endpoint:
 *   1. action=request  { phone }                              -> { otp_id }
 *   2. action=verify   { otp_id, code, name, email, consent }  -> { ok, message }
 *
 * Step 1 only fires once every required field is filled and the privacy box is
 * ticked - an incomplete form makes the button a no-op, by design.
 *
 * `free-entry-otp` is deliberately NOT a registered custom element: it is a
 * child of this one, and customElements.define() upgrades synchronously, so a
 * sibling class defined later in the same file would not be upgraded yet inside
 * this connectedCallback. Keeping the dialog as plain markup driven from here
 * sidesteps that ordering trap entirely.
 */
if (!customElements.get('free-entry-form')) {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  // Plain E.164: a leading `+`, a non-zero country code, 8-15 digits in total.
  const E164_PATTERN = /^\+[1-9]\d{7,14}$/;
  // A Bulgarian subscriber number is exactly 9 digits, so +359 gets its own check.
  const BG_CODE = '+359';
  const BG_PATTERN = /^\+359\d{9}$/;
  const CODE_LENGTH = 6;
  /* Remembers a confirmed entry in this browser for a year. Deliberately not a
   * security control: a private window or cleared cookies gets a fresh form,
   * and the opt-in endpoint stays the real one-entry-per-person authority. This
   * only spares a returning visitor from filling the form out again. */
  const ENTRY_COOKIE = 'wtp_free_entry';
  const ENTRY_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_REVISION = '2026-07-15';

  class FreeEntryForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-free-entry-form]');
      this.nameInput = this.querySelector('[data-free-entry-name]');
      this.emailInput = this.querySelector('[data-free-entry-email]');
      this.phoneInput = this.querySelector('[data-free-entry-phone]');
      this.consentInput = this.querySelector('[data-free-entry-consent]');
      this.marketingInput = this.querySelector('[data-free-entry-marketing]');
      this.submitButton = this.querySelector('.free-entry-submit');
      this.formError = this.querySelector('[data-free-entry-error]');

      this.modal = this.querySelector('[data-free-entry-otp]');
      this.dialog = this.modal?.querySelector('[role="dialog"]');
      this.closeButton = this.modal?.querySelector('[data-free-entry-otp-close]');
      this.phoneLabel = this.modal?.querySelector('[data-free-entry-otp-phone]');
      this.digits = Array.from(this.modal?.querySelectorAll('[data-free-entry-otp-digit]') || []);
      this.digitsWrap = this.modal?.querySelector('[data-free-entry-otp-inputs]');
      this.otpError = this.modal?.querySelector('[data-free-entry-otp-error]');
      this.otpSuccess = this.modal?.querySelector('[data-free-entry-otp-success]');
      this.resendButton = this.modal?.querySelector('[data-free-entry-otp-resend]');
      this.resendWrap = this.modal?.querySelector('[data-free-entry-otp-resend-wrap]');
      this.otpDescription = this.modal?.querySelector('[data-free-entry-otp-description]');
      this.otpHeader = this.modal?.querySelector('[data-free-entry-otp-header]');

      this.fieldsWrap = this.querySelector('[data-free-entry-fields]');
      this.consentsWrap = this.querySelector('[data-free-entry-consents]');
      this.notice = this.querySelector('[data-free-entry-notice]');
      this.expiryLabel = this.modal?.querySelector('[data-free-entry-otp-expiry]');
      this.confirmButton = this.modal?.querySelector('.free-entry-otp-submit');

      this.countryCode = (this.dataset.countryCode || '+359').trim();
      this.ttl = parseInt(this.dataset.otpTtl, 10) || 60;
      this.otpId = null;
      this.pending = false;

      this.form?.addEventListener('submit', this.onSubmit.bind(this));
      this.closeButton?.addEventListener('click', () => this.closeModal());
      // The scrim is the host's own background, so a click that lands on the
      // host itself (not the dialog inside it) is a click outside.
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
      this.resendButton?.addEventListener('click', this.onResend.bind(this));
      this.confirmButton?.addEventListener('click', this.onConfirm.bind(this));
      this.onKeyDown = this.onKeyDown.bind(this);

      // Clearing the invalid mark as soon as the field is touched keeps the
      // "button does nothing" state from feeling permanent. Clearing the custom
      // validity matters more: a message set via setCustomValidity keeps the
      // field invalid until it is reset to '', so leaving it would block the
      // form for good even once the value is fixed.
      [this.nameInput, this.emailInput, this.phoneInput].forEach((input) => {
        input?.addEventListener('input', () => this.setFieldValidity(input, ''));
      });

      // Reflecting the normalized number back into the field on blur shows the
      // user the exact E.164 number the code will be sent to.
      this.phoneInput?.addEventListener('blur', () => {
        if (!this.isValidPhone(this.phoneInput.value)) return;
        this.phoneInput.value = this.normalizePhone(this.phoneInput.value);
      });
      this.consentInput?.addEventListener('change', () => {
        this.setFieldValidity(this.consentInput, '');
      });

      this.bindDigits();

      // A returning visitor sees the "already in" state straight away, without
      // being invited to fill the form out a second time.
      if (this.hasEntered()) this.showAlreadyEntered();
    }

    disconnectedCallback() {
      this.stopCountdown();
      this.stopListeningForSmsCode();
      document.removeEventListener('keydown', this.onKeyDown);
      this.unlockScroll();
    }

    /* ------------------------------------------------------------ validation */

    /* Separators are dropped, then the prefix is resolved: `+` is already E.164,
     * `00` is its international-access spelling, and a leading `0` or a bare
     * subscriber number is local, so it takes the shop's country code. */
    normalizePhone(raw) {
      const trimmed = (raw || '').replace(/[\s()\-.]/g, '');
      if (!trimmed) return '';
      if (trimmed.startsWith('+')) return trimmed;
      if (trimmed.startsWith('00')) return `+${trimmed.slice(2)}`;
      if (trimmed.startsWith('0')) return `${this.countryCode}${trimmed.slice(1)}`;
      return `${this.countryCode}${trimmed}`;
    }

    isValidPhone(raw) {
      const e164 = this.normalizePhone(raw);
      return e164.startsWith(BG_CODE) ? BG_PATTERN.test(e164) : E164_PATTERN.test(e164);
    }

    /* Pushes our own rules into the field's constraint validation, so the
     * browser reports them in its native bubble - `required` and `type=email`
     * alone can't express "9 digits after +359", and a bare red border never
     * told anyone WHAT was wrong. */
    setFieldValidity(field, message) {
      if (!field) return;

      field.setCustomValidity(message || '');
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
    }

    isValid() {
      const name = (this.nameInput?.value || '').trim();
      const email = (this.emailInput?.value || '').trim();
      const phone = (this.phoneInput?.value || '').trim();

      // An empty field reads as "fill this in"; a filled but malformed one gets
      // the message specific to what it is.
      this.setFieldValidity(this.nameInput, name.length > 1 ? '' : this.dataset.errorRequired);
      this.setFieldValidity(
        this.emailInput,
        EMAIL_PATTERN.test(email) ? '' : email ? this.dataset.errorEmail : this.dataset.errorRequired
      );
      this.setFieldValidity(
        this.phoneInput,
        this.isValidPhone(phone) ? '' : phone ? this.dataset.errorPhone : this.dataset.errorRequired
      );
      this.setFieldValidity(
        this.consentInput,
        this.consentInput?.checked ? '' : this.dataset.errorConsent
      );

      // The form carries `novalidate`, which only suppresses validation during
      // the browser's own submit - calling reportValidity() explicitly still
      // focuses the first offending field and shows its bubble.
      return this.form ? this.form.reportValidity() : true;
    }

    /* --------------------------------------------------------------- network */

    endpoint(action) {
      const base = this.dataset.endpoint;
      const key = encodeURIComponent(this.dataset.apiKey || '');
      const form = encodeURIComponent(this.dataset.form || '');
      return `${base}?key=${key}&form=${form}&action=${action}`;
    }

    async post(action, body) {
      const response = await fetch(this.endpoint(action), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (error) {
        payload = {};
      }

      return { response, payload };
    }

    async onSubmit(event) {
      event.preventDefault();

      // An incomplete form is a no-op: no request, no dialog. So is a repeat
      // submit once the entry is confirmed - one free entry per person, and the
      // dialog's code boxes no longer exist.
      if (this.pending || this.completed || !this.isValid()) return;

      this.setError(this.formError, '');
      this.setPending(this.submitButton, true);

      try {
        const phone = this.normalizePhone(this.phoneInput.value);
        const { payload } = await this.post('request', { phone });

        if (!payload.otp_id) {
          this.setError(this.formError, payload.message || this.dataset.errorGeneric);
          return;
        }

        this.otpId = payload.otp_id;
        this.openModal(phone);
      } catch (error) {
        this.setError(this.formError, this.dataset.errorNetwork);
      } finally {
        this.setPending(this.submitButton, false);
      }
    }

    async onResend(event) {
      event.preventDefault();
      if (this.pending) return;

      this.setError(this.otpError, '');
      this.resendButton.disabled = true;

      try {
        const phone = this.normalizePhone(this.phoneInput.value);
        const { payload } = await this.post('request', { phone });

        if (payload.otp_id) {
          this.otpId = payload.otp_id;
          this.clearDigits();
          this.startCountdown();
          // A resend means a fresh SMS, so re-arm the reader for it.
          this.listenForSmsCode();
          this.digits[0]?.focus();
        } else {
          this.setError(this.otpError, payload.message || this.dataset.errorGeneric);
        }
      } catch (error) {
        this.setError(this.otpError, this.dataset.errorNetwork);
      } finally {
        this.resendButton.disabled = false;
      }
    }

    async onConfirm(event) {
      event.preventDefault();

      const code = this.digits.map((input) => input.value).join('');
      // A short code is a no-op, same rule as the first step.
      if (this.pending || code.length < CODE_LENGTH) return;

      this.setError(this.otpError, '');
      this.setPending(this.confirmButton, true);

      try {
        const { payload } = await this.post('verify', {
          otp_id: this.otpId,
          code,
          name: this.nameInput.value.trim(),
          email: this.emailInput.value.trim(),
          consent: Boolean(this.consentInput?.checked),
          marketing: Boolean(this.marketingInput?.checked),
        });

        if (payload.ok) {
          // Deliberately not awaited: the list subscription is a side effect of
          // a confirmed entry, so it must never delay the success state - nor
          // cost the entrant their entry if Klaviyo is down.
          this.subscribeToKlaviyo();
          this.onVerified(payload.message);
          return;
        }

        this.digitsWrap?.setAttribute('aria-invalid', 'true');
        this.setError(this.otpError, payload.message || this.errorFor(payload.code));
      } catch (error) {
        this.setError(this.otpError, this.dataset.errorNetwork);
      } finally {
        this.setPending(this.confirmButton, false);
      }
    }

    /* ---------------------------------------------------------- entry memory */

    hasEntered() {
      return document.cookie
        .split('; ')
        .some((entry) => entry.startsWith(`${ENTRY_COOKIE}=`));
    }

    rememberEntry() {
      document.cookie = `${ENTRY_COOKIE}=1; max-age=${ENTRY_COOKIE_MAX_AGE}; path=/; samesite=lax`;
    }

    /* Swaps the form itself for the "already in" line. Reached both from a fresh
     * confirmation (once the dialog closes) and on load for a returning
     * visitor, so the answer is the same either way. */
    showAlreadyEntered() {
      this.completed = true;

      [this.fieldsWrap, this.consentsWrap, this.submitButton].forEach((node) => {
        if (node) node.hidden = true;
      });
      this.setError(this.formError, '');

      const message = this.dataset.alreadyEntered || '';
      if (this.notice) {
        this.notice.textContent = message;
        this.notice.hidden = !message;
      }
    }

    /* --------------------------------------------------------------- klaviyo */

    /* Adds the entrant to the configured Klaviyo list, once their phone is
     * verified. Runs against the public client endpoint, which takes only the
     * public API key (the same one klaviyo.js exposes) and is safe in the
     * browser - never put a private key here. */
    async subscribeToKlaviyo() {
      const listId = (this.dataset.klaviyoListId || '').trim();
      const companyId = (this.dataset.klaviyoPublicKey || '').trim();

      // The optional marketing box IS the consent record. Someone who leaves it
      // unticked still enters the draw, but never lands on the marketing list.
      if (!listId || !companyId || !this.marketingInput?.checked) return;

      const email = this.emailInput.value.trim();
      if (!email) return;

      const attributes = {
        email,
        // Email only: Klaviyo requires SMS consent to be declared alongside any
        // phone_number, and this checkbox is not an SMS marketing opt-in.
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      };

      const name = this.nameInput.value.trim();
      const split = name.indexOf(' ');
      if (split > 0) {
        attributes.first_name = name.slice(0, split);
        attributes.last_name = name.slice(split + 1).trim();
      } else if (name) {
        attributes.first_name = name;
      }

      const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.klaviyoSource || 'Free entry form',
                profile: { data: { type: 'profile', attributes } },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        });

        if (!response.ok) {
          console.warn('[free-entry] Klaviyo rejected the subscription', response.status);
        }
      } catch (error) {
        console.warn('[free-entry] Klaviyo subscription failed', error);
      }
    }

    errorFor(code) {
      if (code === 'invalid_code') return this.dataset.errorInvalidCode;
      return this.dataset.errorGeneric;
    }

    onVerified(message) {
      this.stopCountdown();
      this.stopListeningForSmsCode();
      this.setError(this.otpError, '');
      // The entry is done; a second submit must not reopen a dialog whose code
      // boxes have been taken out of the DOM.
      this.completed = true;

      // Taken out of the DOM, not just hidden. The resend block is reached by
      // its own wrapper rather than via the resend button, which is absent
      // whenever the resend label setting is blank.
      [this.digitsWrap, this.resendWrap].forEach((node) => node?.remove());
      this.digits = [];
      this.digitsWrap = null;
      this.resendWrap = null;
      this.resendButton = null;
      this.expiryLabel = null;

      // These stay in the DOM. "Enter the 6-digit code" contradicts the success
      // message, and the confirm button has nothing left to confirm.
      [this.otpDescription, this.confirmButton].forEach((node) => {
        if (node) node.hidden = true;
      });

      if (this.otpSuccess) {
        this.otpSuccess.textContent = message || this.dataset.successMessage || '';
        this.otpSuccess.hidden = !this.otpSuccess.textContent;
        this.moveSuccessIntoHeader();
      }

      // Remembered now rather than on close, so the entry survives a visitor
      // who navigates away with the dialog still open.
      this.rememberEntry();

      this.dispatchEvent(new CustomEvent('free-entry:verified', { bubbles: true }));
    }

    /* Clears the header - icon, title and the "sent to +359..." line, all of
     * which are stale once the code is confirmed - and puts the success message
     * in their place. The close button is the one thing kept: without it the
     * dialog could not be dismissed. */
    moveSuccessIntoHeader() {
      if (!this.otpHeader || !this.otpSuccess) return;

      Array.from(this.otpHeader.children).forEach((child) => {
        if (child !== this.closeButton) child.remove();
      });
      this.otpHeader.append(this.otpSuccess);

      // The title it replaced was the dialog's accessible name.
      if (this.otpSuccess.id) this.dialog?.setAttribute('aria-labelledby', this.otpSuccess.id);
    }

    /* ----------------------------------------------------------------- dialog */

    openModal(phone) {
      if (!this.modal) return;

      if (this.phoneLabel) this.phoneLabel.textContent = this.maskPhone(phone);

      this.clearDigits();
      this.setError(this.otpError, '');
      this.modal.hidden = false;
      this.lockScroll();
      this.startCountdown();
      this.listenForSmsCode();
      document.addEventListener('keydown', this.onKeyDown);

      if (typeof trapFocus === 'function') {
        trapFocus(this.modal, this.digits[0] || this.dialog);
      } else {
        (this.digits[0] || this.dialog)?.focus();
      }
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      this.modal.hidden = true;
      this.stopCountdown();
      this.stopListeningForSmsCode();
      this.unlockScroll();
      document.removeEventListener('keydown', this.onKeyDown);

      // Closing after a confirmed entry hands the visitor back to a form that
      // now says they are already in, rather than an empty one to refill.
      if (this.completed) this.showAlreadyEntered();

      // The submit button is hidden once the entry is in, so focus would land
      // nowhere; the notice that replaced it takes it instead.
      let restoreTo = this.submitButton;
      if (this.completed && this.notice && !this.notice.hidden) {
        this.notice.tabIndex = -1;
        restoreTo = this.notice;
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(restoreTo);
      } else {
        restoreTo?.focus();
      }
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closeModal();
    }

    maskPhone(e164) {
      const rest = e164.startsWith(this.countryCode)
        ? e164.slice(this.countryCode.length)
        : e164.replace(/^\+/, '');

      if (rest.length < 6) return e164;
      return `${this.countryCode} ${rest.slice(0, 2)} *** ${rest.slice(-4)}`;
    }

    /* ------------------------------------------------------------ code inputs */

    bindDigits() {
      this.digits.forEach((input, index) => {
        input.addEventListener('input', () => {
          const raw = input.value.replace(/\D/g, '');

          // More than one digit means the value arrived whole rather than being
          // typed: an SMS keyboard suggestion, WebOTP, or a paste. Spread it.
          if (raw.length > 1) {
            this.fillDigits(raw, raw.length >= CODE_LENGTH ? 0 : index);
            return;
          }

          input.value = raw;
          input.classList.toggle('is-filled', raw !== '');
          this.digitsWrap?.removeAttribute('aria-invalid');
          this.setError(this.otpError, '');

          if (raw && index < this.digits.length - 1) this.digits[index + 1].focus();
        });

        input.addEventListener('keydown', (event) => {
          if (event.key === 'Backspace' && !input.value && index > 0) {
            event.preventDefault();
            this.digits[index - 1].focus();
            this.digits[index - 1].value = '';
            this.digits[index - 1].classList.remove('is-filled');
          }

          if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.digits[index - 1].focus();
          }

          if (event.key === 'ArrowRight' && index < this.digits.length - 1) {
            event.preventDefault();
            this.digits[index + 1].focus();
          }
        });

        input.addEventListener('focus', () => input.select());

        input.addEventListener('paste', (event) => {
          event.preventDefault();
          const pasted = (event.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
          if (!pasted) return;

          this.fillDigits(pasted, pasted.length >= CODE_LENGTH ? 0 : index);
        });
      });
    }

    /* Spreads a whole code across the boxes from `start`, then parks the caret
     * on the box after the last one filled. */
    fillDigits(code, start = 0) {
      this.digits.forEach((digit, offset) => {
        if (offset < start) return;
        const char = code[offset - start] || '';
        digit.value = char;
        digit.classList.toggle('is-filled', char !== '');
      });

      this.digitsWrap?.removeAttribute('aria-invalid');
      this.setError(this.otpError, '');
      this.digits[Math.min(start + code.length, this.digits.length - 1)]?.focus();
    }

    /* Android Chrome can lift the code straight out of the arriving SMS with no
     * keyboard tap at all - but only when the message's last line binds it to
     * this origin, e.g. `@what-the-pink.myshopify.com #472819`. Until the
     * Supabase function sends that line this is inert: no prompt appears and
     * the promise never settles, so asking costs nothing and it starts working
     * the day the message format changes. iOS ignores this entirely and uses
     * the `one-time-code` keyboard suggestion instead. */
    listenForSmsCode() {
      if (!('OTPCredential' in window) || !window.isSecureContext) return;

      this.stopListeningForSmsCode();
      this.smsAbort = new AbortController();

      navigator.credentials
        .get({ otp: { transport: ['sms'] }, signal: this.smsAbort.signal })
        .then((credential) => {
          const code = (credential?.code || '').replace(/\D/g, '');
          if (code) this.fillDigits(code, 0);
        })
        .catch(() => {
          /* Aborted on close, dismissed by the visitor, or unsupported. */
        });
    }

    stopListeningForSmsCode() {
      this.smsAbort?.abort();
      this.smsAbort = null;
    }

    clearDigits() {
      this.digits.forEach((input) => {
        input.value = '';
        input.classList.remove('is-filled');
      });
      this.digitsWrap?.removeAttribute('aria-invalid');
    }

    /* ------------------------------------------------------------- countdown */

    startCountdown() {
      this.stopCountdown();
      if (!this.expiryLabel) return;

      let remaining = this.ttl;
      const template = this.expiryLabel.dataset.template || '';

      const tick = () => {
        const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
        const seconds = String(remaining % 60).padStart(2, '0');
        this.expiryLabel.textContent = template.replace('{time}', `${minutes}:${seconds}`);

        if (remaining <= 0) {
          this.stopCountdown();
          return;
        }
        remaining -= 1;
      };

      tick();
      this.countdown = setInterval(tick, 1000);
    }

    stopCountdown() {
      if (!this.countdown) return;
      clearInterval(this.countdown);
      this.countdown = null;
    }

    /* ----------------------------------------------------------------- utils */

    setPending(button, pending) {
      this.pending = pending;
      if (!button) return;
      button.setAttribute('aria-busy', pending ? 'true' : 'false');
      button.disabled = pending;
    }

    setError(node, message) {
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
    }

    lockScroll() {
      this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
      if (this.bodyWasLocked) return;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      this.previousPaddingRight = document.body.style.paddingRight;

      if (scrollbarWidth > 0) {
        const current = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
        document.body.style.paddingRight = `${current + scrollbarWidth}px`;
      }

      document.body.classList.add('overflow-hidden');
    }

    unlockScroll() {
      if (this.bodyWasLocked) return;
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = this.previousPaddingRight || '';
    }
  }

  customElements.define('free-entry-form', FreeEntryForm);
}
